// @systeme: contrat
// Les récurrences : le modèle d'un élément qui revient, et la règle qui dit quand.
import { z } from 'zod';
import { SchemaCategorieElement, SchemaFrequenceRecurrence, SchemaSensibilite, SchemaVisibilite, } from '../enumerations.js';
import { SchemaHeure, SchemaInstant, SchemaJour } from './elements.js';
const SchemaJourSemaine = z.number().int().min(1).max(7); // lundi = 1 … dimanche = 7
/**
 * La cadence en un clic, telle qu'on la dit : « chaque semaine », « une semaine sur
 * deux », « chaque mois », « chaque année », « chaque jour ». Le serveur en fait une
 * règle complète à partir du jour de la fiche.
 */
export const Cadences = ['jour', 'semaine', 'quinzaine', 'mois', 'annee'];
export const SchemaCadence = z.enum(Cadences);
/**
 * La règle de récurrence, sous-ensemble volontairement petit de RRULE :
 * - quotidien : tous les N jours ;
 * - hebdomadaire : tous les N semaines, ces jours-là (joursSemaine) ;
 * - mensuel : tous les N mois, le jour X (jourMois) ou « le dernier vendredi » (position) ;
 * - annuel : tous les ans, tel jour de tel mois.
 * Toutes les dates sont calculées par le moteur de temps, en Europe/Paris.
 */
const SchemaRegleRecurrenceBase = z.object({
    frequence: SchemaFrequenceRecurrence,
    intervalle: z.number().int().min(1).max(52).default(1),
    /** Hebdomadaire : les jours concernés. */
    joursSemaine: z.array(SchemaJourSemaine).min(1).max(7).optional(),
    /** Mensuel : le jour du mois (1 à 31, borné au dernier jour) ou « dernier ». */
    jourMois: z.union([z.number().int().min(1).max(31), z.literal('dernier')]).optional(),
    /** Mensuel : « le dernier vendredi » = { n: -1, jourSemaine: 5 } ; « le 2e mardi » = { n: 2, jourSemaine: 2 }. */
    position: z
        .object({
        n: z.union([z.literal(-1), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
        jourSemaine: SchemaJourSemaine,
    })
        .optional(),
    /** Annuel : le mois (1 à 12) et le jour. */
    mois: z.number().int().min(1).max(12).optional(),
    jour: z.number().int().min(1).max(31).optional(),
    /** L'heure de l'occurrence, ou nulle pour « journée entière ». */
    heure: SchemaHeure.nullable().default(null),
    /** Première date possible (AAAA-MM-JJ) et dernière (facultative). */
    debut: SchemaJour,
    fin: SchemaJour.nullable().default(null),
});
export const SchemaRegleRecurrence = SchemaRegleRecurrenceBase.superRefine((r, ctx) => {
    if (r.frequence === 'hebdomadaire' && !r.joursSemaine)
        ctx.addIssue({
            code: 'custom',
            message: 'Quels jours de la semaine ?',
            path: ['joursSemaine'],
        });
    if (r.frequence === 'mensuel' && r.jourMois === undefined && !r.position)
        ctx.addIssue({ code: 'custom', message: 'Quel jour du mois ?', path: ['jourMois'] });
    if (r.frequence === 'annuel' && (r.mois === undefined || r.jour === undefined))
        ctx.addIssue({ code: 'custom', message: 'Quelle date dans l’année ?', path: ['mois'] });
});
/** Les champs de l'élément à créer à chaque occurrence. */
export const SchemaGabaritRecurrence = z.object({
    titre: z.string().trim().min(1, { error: 'Il manque un titre.' }).max(200),
    type: z.enum(['tache', 'evenement']).default('tache'),
    categorie: SchemaCategorieElement.nullable().default(null),
    detail: z.string().trim().max(5000).nullable().default(null),
    visibilite: SchemaVisibilite.default('espace'),
    sensibilite: SchemaSensibilite.default('normale'),
    personneId: z.uuid().nullable().default(null),
});
export const SchemaCreationRecurrence = z.object({
    gabarit: SchemaGabaritRecurrence,
    regle: SchemaRegleRecurrence,
});
export const SchemaMajRecurrence = z
    .object({
    gabarit: SchemaGabaritRecurrence,
    regle: SchemaRegleRecurrence,
    active: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { error: 'Rien à modifier.' });
export const SchemaRecurrence = z.object({
    id: z.uuid(),
    espaceId: z.uuid(),
    gabarit: SchemaGabaritRecurrence,
    regle: SchemaRegleRecurrence,
    /** La règle en français (« chaque mercredi »), écrite par le serveur. */
    description: z.string(),
    horizonJours: z.number().int(),
    derniereGenerationLe: SchemaInstant.nullable(),
    active: z.boolean(),
    /** La prochaine occurrence à venir, calculée par le serveur. */
    prochaineOccurrence: SchemaInstant.nullable(),
    creeLe: SchemaInstant,
});
/** Une récurrence prête à l'emploi du catalogue (poubelles, factures, entretien…). */
export const SchemaModeleRecurrence = z.object({
    cle: z.string(),
    nom: z.string(),
    description: z.string(),
    famille: z.enum(['poubelles', 'factures', 'maison', 'vehicule', 'assurances', 'autre']),
    gabarit: SchemaGabaritRecurrence,
    /** La règle de départ ; le client complète « debut » et, pour les poubelles, les jours (vides ici). */
    regle: SchemaRegleRecurrenceBase.omit({ debut: true })
        .partial()
        .extend({ joursSemaine: z.array(SchemaJourSemaine).max(7).optional() }),
});
//# sourceMappingURL=recurrences.js.map